// src/pages/OrderSuccess.jsx — Epic Games Store style
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { getOrder } from "../services/orderService";
import { useAuth } from "../context/AuthContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.os-pg { min-height:100vh; background:#121212; color:#fff; font-family:'Inter',sans-serif; }

.os-body { max-width:720px; margin:0 auto; padding:100px 24px 80px; }

/* Success header */
.os-header { text-align:center; margin-bottom:48px; animation:fadeUp 0.4s ease both; }
@keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

.os-check {
  width:72px; height:72px; background:rgba(34,197,94,0.1); border:2px solid rgba(34,197,94,0.3);
  border-radius:50%; display:flex; align-items:center; justify-content:center;
  font-size:32px; margin:0 auto 20px;
  animation:pop 0.5s cubic-bezier(0.16,1,0.3,1) both; position:relative;
}
@keyframes pop { from{transform:scale(0.4);opacity:0} to{transform:scale(1);opacity:1} }
.os-check::after { content:''; position:absolute; inset:-8px; border:1px solid rgba(34,197,94,0.12); border-radius:50%; animation:ripple 2.5s ease infinite; }
@keyframes ripple { 0%{transform:scale(1);opacity:1} 100%{transform:scale(1.6);opacity:0} }

.os-title { font-size:36px; font-weight:900; color:#fff; letter-spacing:-1px; margin-bottom:8px; }
.os-sub   { font-size:14px; color:#6b6b6b; font-weight:400; margin-bottom:14px; }
.os-game-pill { display:inline-flex; align-items:center; gap:6px; background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25); border-radius:4px; padding:6px 14px; font-size:13px; font-weight:600; color:#0078F2; }

/* Credentials card */
.creds-card { background:#1a1a1a; border:1px solid rgba(34,197,94,0.2); border-radius:12px; overflow:hidden; margin-bottom:16px; animation:fadeUp 0.4s 0.1s ease both; box-shadow:0 0 32px rgba(34,197,94,0.04); }
.creds-hdr { padding:16px 24px; background:rgba(34,197,94,0.05); border-bottom:1px solid rgba(34,197,94,0.1); display:flex; align-items:center; gap:10px; }
.creds-hdr-title { font-size:14px; font-weight:700; color:#4ade80; }
.creds-hdr-sub   { font-size:12px; color:#6b6b6b; margin-top:2px; }
.creds-body { padding:24px; display:flex; flex-direction:column; gap:20px; }

.cred-field {}
.cred-label { font-size:11px; font-weight:600; color:#4a4a4a; text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
.cred-row { display:flex; align-items:center; gap:10px; }
.cred-val {
  flex:1; background:#121212; border:1px solid rgba(255,255,255,0.08); border-radius:8px;
  padding:13px 16px; font-family:'Inter',sans-serif; font-size:15px; font-weight:600; color:#fff;
  word-break:break-all; letter-spacing:0.3px;
}
.copy-btn {
  width:42px; height:42px; flex-shrink:0;
  background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.1);
  border-radius:8px; color:#6b6b6b; cursor:pointer; font-size:16px;
  display:flex; align-items:center; justify-content:center; transition:all 0.15s;
}
.copy-btn:hover { background:rgba(0,120,242,0.1); border-color:rgba(0,120,242,0.3); color:#0078F2; }
.copy-btn.copied { background:rgba(34,197,94,0.1); border-color:rgba(34,197,94,0.3); color:#4ade80; }

.copy-all-btn {
  width:100%; margin-top:4px; padding:11px;
  background:rgba(34,197,94,0.08); border:1px solid rgba(34,197,94,0.2);
  border-radius:8px; color:#4ade80;
  font-family:'Inter',sans-serif; font-size:13px; font-weight:700;
  cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px;
  transition:all 0.15s;
}
.copy-all-btn:hover { background:rgba(34,197,94,0.14); border-color:rgba(34,197,94,0.35); }
.copy-all-btn.copied { background:rgba(34,197,94,0.18); color:#22c55e; }

.cred-note { background:rgba(234,179,8,0.06); border:1px solid rgba(234,179,8,0.15); border-radius:8px; padding:12px 14px; font-size:13px; color:#eab308; line-height:1.6; }

/* Warning */
.warn-card { background:#1a1a1a; border:1px solid rgba(239,68,68,0.15); border-radius:12px; padding:20px 24px; margin-bottom:16px; animation:fadeUp 0.4s 0.2s ease both; }
.warn-title { font-size:14px; font-weight:700; color:#f87171; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
.warn-list { display:flex; flex-direction:column; gap:8px; }
.warn-item { display:flex; align-items:flex-start; gap:10px; font-size:13px; color:#6b6b6b; line-height:1.55; }
.warn-dot { width:6px; height:6px; background:#ef4444; border-radius:50%; flex-shrink:0; margin-top:5px; }

/* How to */
.howto-card { background:#1a1a1a; border:1px solid rgba(255,255,255,0.07); border-radius:12px; padding:24px; margin-bottom:24px; animation:fadeUp 0.4s 0.3s ease both; }
.howto-title { font-size:16px; font-weight:700; color:#fff; margin-bottom:20px; display:flex; align-items:center; gap:8px; }
.howto-steps { display:flex; flex-direction:column; gap:0; }
.howto-step { display:flex; gap:14px; align-items:flex-start; padding:14px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
.howto-step:last-child { border-bottom:none; }
.howto-num { width:28px; height:28px; background:#0078F2; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#fff; flex-shrink:0; }
.howto-icon { font-size:18px; flex-shrink:0; }
.howto-step-title { font-size:14px; font-weight:700; color:#fff; margin-bottom:3px; }
.howto-step-desc  { font-size:13px; color:#6b6b6b; line-height:1.55; }

/* Actions */
.os-actions { display:flex; gap:12px; flex-wrap:wrap; animation:fadeUp 0.4s 0.4s ease both; }
.btn-orders {
  flex:1; padding:14px 20px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1);
  border-radius:8px; color:#a3a3a3; font-family:'Inter',sans-serif; font-size:14px; font-weight:700;
  text-decoration:none; text-align:center; transition:all 0.15s;
}
.btn-orders:hover { border-color:rgba(255,255,255,0.2); color:#fff; }
.btn-chat {
  flex:1; padding:14px 20px; background:#0078F2; border:none; border-radius:8px;
  color:#fff; font-family:'Inter',sans-serif; font-size:14px; font-weight:700;
  text-decoration:none; text-align:center; transition:all 0.15s; cursor:pointer;
}
.btn-chat:hover { background:#0a84ff; transform:translateY(-1px); }

.os-order-meta { text-align:center; margin-top:16px; font-size:12px; color:#3a3a3a; }

.skel { background:#1a1a1a; border-radius:12px; animation:shim 1.5s ease infinite; }
@keyframes shim { 0%,100%{opacity:.5} 50%{opacity:.8} }

@media(max-width:680px){ .os-body { padding:90px 16px 60px; } .creds-body { padding:18px; } }
`;

const STEPS = [
  { icon:"💻", title:"Open Steam",         desc:'Click "Sign in to a different account" on the Steam login screen.' },
  { icon:"🔑", title:"Enter Credentials",  desc:"Use the Steam Email and Password shown above to log in." },
  { icon:"🎮", title:"Install the Game",   desc:"Go to your Library, find the game, and click Install." },
  { icon:"📴", title:"Go Offline",         desc:'Go to Steam → top-left menu → "Go Offline". Steam will restart.' },
  { icon:"🏆", title:"Play!",              desc:"Log back into your own Steam account. Launch the game offline. Enjoy!" },
];

export default function OrderSuccess() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopied] = useState("");

  useEffect(() => {
    if (!orderId) return;
    getOrder(orderId).then(o => {
      if (!o || o.userId !== user?.uid) { navigate("/orders"); return; }
      setOrder(o); setLoading(false);
    }).catch(() => navigate("/orders"));
  }, [orderId, user]);

  function copy(text, field) {
    navigator.clipboard.writeText(text).then(() => { setCopied(field); setTimeout(() => setCopied(""), 2200); });
  }

  function copyAll() {
    const text = `Game: ${order.gameTitle}\nEmail: ${order.steamEmail}\nPassword: ${order.steamPassword}${order.steamNote ? `\nNote: ${order.steamNote}` : ""}`;
    copy(text, "all");
  }

  return (
    <>
      <style>{S}</style>
      <div className="os-pg">
        <Navbar />
        <div className="os-body">
          {loading ? (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {[160,220,180].map((h,i) => <div key={i} className="skel" style={{height:h}} />)}
            </div>
          ) : order && (
            <>
              <div className="os-header">
                <div className="os-check">✅</div>
                <h1 className="os-title">Payment Confirmed!</h1>
                <p className="os-sub">Your credentials are ready — save them somewhere safe</p>
                <div className="os-game-pill">🎮 {order.gameTitle}</div>
              </div>

              {/* Credentials */}
              <div className="creds-card">
                <div className="creds-hdr">
                  <span style={{fontSize:18}}>🔑</span>
                  <div>
                    <div className="creds-hdr-title">Steam Account Credentials</div>
                    <div className="creds-hdr-sub">Keep these private and safe!</div>
                  </div>
                </div>
                <div className="creds-body">
                  <div className="cred-field">
                    <div className="cred-label">Steam Email</div>
                    <div className="cred-row">
                      <div className="cred-val">{order.steamEmail}</div>
                      <button className={`copy-btn ${copiedField==="email"?"copied":""}`} onClick={()=>copy(order.steamEmail,"email")}>{copiedField==="email"?"✓":"📋"}</button>
                    </div>
                  </div>
                  <div className="cred-field">
                    <div className="cred-label">Steam Password</div>
                    <div className="cred-row">
                      <div className="cred-val">{order.steamPassword}</div>
                      <button className={`copy-btn ${copiedField==="password"?"copied":""}`} onClick={()=>copy(order.steamPassword,"password")}>{copiedField==="password"?"✓":"📋"}</button>
                    </div>
                  </div>
                  {order.steamNote && <div className="cred-note">📝 <strong>Note:</strong> {order.steamNote}</div>}

                  {/* Copy All button */}
                  <button
                    className={`copy-all-btn ${copiedField === "all" ? "copied" : ""}`}
                    onClick={copyAll}
                  >
                    {copiedField === "all" ? "✓ Copied!" : "📋 Copy All Credentials"}
                  </button>
                </div>
              </div>

              {/* Warning */}
              <div className="warn-card">
                <div className="warn-title">⚠️ Important Rules</div>
                <div className="warn-list">
                  {["Do NOT change the password or any account details","This is a shared account — do NOT use it online","Do NOT add 2FA or link your phone number","Do NOT purchase anything using this account","Always switch to Offline Mode before playing"].map((t,i) => (
                    <div key={i} className="warn-item"><div className="warn-dot"/>{t}</div>
                  ))}
                </div>
              </div>

              {/* Steps */}
              <div className="howto-card">
                <div className="howto-title">📖 How to Play</div>
                <div className="howto-steps">
                  {STEPS.map((s,i) => (
                    <div key={i} className="howto-step">
                      <div className="howto-num">{i+1}</div>
                      <div className="howto-icon">{s.icon}</div>
                      <div>
                        <div className="howto-step-title">{s.title}</div>
                        <div className="howto-step-desc">{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="os-actions">
                <Link to="/orders" className="btn-orders">📦 My Orders</Link>
                <Link to={`/orders/${orderId}/chat`} className="btn-chat">💬 Need Help? Chat Now</Link>
              </div>
              <div className="os-order-meta">Order #{orderId} · ${order.amount} USD · {order.gameTitle}</div>
            </>
          )}
        </div>
      </div>
    </>
  );
}